import SublineModel from '@/editor/entities/2d/sublineModel';
import {
  IAbsorbResult,
  IBoxBoundingLineInfo,
  ICenterLineInfo,
  RotatedBoxAbsorbCalculator
} from './rotatedBoxAbsorbCalculator';
import { LineSeg2d } from '@/editor/math/LineSeg2d';
import { globalStores } from '@/editor/stores';
import { convertParallelAbsorbSeb, createBoxParallelAbsorbSegs, getLineAngle } from './utils';
import { ESeparateWallType } from '@/editor/data/separateWall/SeparateWall';
import { EFaceSide, IRotatedBox } from '@/editor/types';

export class AssemblyAbsorbManager {
  private _calculator = new RotatedBoxAbsorbCalculator();
  private _sublineModel = new SublineModel();

  constructor() {}

  updateData(params?: { excludeAssemblyIds: string[] }) {
    const excludeAssemblyIds = params?.excludeAssemblyIds ?? [];
    const absorbBoxBoundingLines: IBoxBoundingLineInfo[] = [];
    const absorbCenterLines: ICenterLineInfo[] = [];

    // 添加区域边界和开洞边界(房间内独立自由墙体视为开洞,开洞边与外轮廓绕向相反,内部同样在边的左侧)
    globalStores.regionStore.getRegionDatas().forEach((region) => {
      const holePathInfos = region.holeInfos.map((holeEdges) => {
        // 房间内开洞保留全部
        if (new Set(holeEdges.map(({ wallId }) => wallId)).size > 1) {
          return holeEdges;
        } else {
          // 单墙体导致的开洞排除端点面
          return holeEdges.filter((edge) => edge.side !== EFaceSide.StartSide && edge.side !== EFaceSide.EndSide);
        }
      });

      [region.pathInfos, ...holePathInfos].forEach((pathInfos) => {
        pathInfos.forEach((pathInfo) => {
          if (pathInfo.sepType === ESeparateWallType.VirtualWall) return; // 虚拟隔墙单独处理
          if (pathInfo.center) return; // 弧形墙/隔墙不参与吸附
          const seg = new LineSeg2d(pathInfo.start, pathInfo.end);
          const line = seg.createLine();
          absorbBoxBoundingLines.push({
            seg,
            line,
            rotatable: true,
            onlyAbsorbSeg: true,
            onlyOnLeftSide: true,
            angle: getLineAngle(line)
          });
        });
      });
    });

    // 添加虚拟隔墙，需要添加反向
    globalStores.separateWallStore.getSepDatas({ onlyNotBase: true }).forEach((sepWallData) => {
      if (sepWallData.type !== ESeparateWallType.VirtualWall) return;
      if (sepWallData.center) return; // 弧形虚拟隔墙不参与吸附
      const seg = new LineSeg2d(sepWallData.start, sepWallData.end);
      const line = seg.createLine();
      absorbBoxBoundingLines.push({
        seg,
        line,
        rotatable: true,
        onlyAbsorbSeg: true,
        onlyOnLeftSide: true,
        angle: getLineAngle(line)
      });

      const oppositeSeg = new LineSeg2d(sepWallData.end, sepWallData.start);
      const oppositeLine = oppositeSeg.createLine();
      absorbBoxBoundingLines.push({
        seg: oppositeSeg,
        line: oppositeLine,
        rotatable: true,
        onlyAbsorbSeg: true,
        onlyOnLeftSide: true,
        angle: getLineAngle(oppositeLine)
      });
    });

    // 添加家具组合的中心线和边界线
    globalStores.assemblyStore.getAssemblyDatas().forEach((assembly) => {
      if (excludeAssemblyIds.includes(assembly.id)) return;

      const rotatedBox = {
        center: assembly.center,
        width: assembly.width,
        thickness: assembly.thickness,
        rotation: assembly.rotation
      };

      const { centerLines, boxBoundingLines } = createBoxParallelAbsorbSegs(rotatedBox);
      centerLines.forEach((centerLine) => {
        absorbCenterLines.push(centerLine);
      });
      boxBoundingLines.forEach((boxBoundingLine) => {
        absorbBoxBoundingLines.push(boxBoundingLine);
      });
    });

    // 添加家具中心线和边界线，排除在当前选中组合内的家具
    globalStores.modelStore.getFurnitureDatas().forEach((furniture) => {
      const furnitureAssemblyId = globalStores.assemblyStore.getAssemblyIdByFunitureId(furniture.id);

      if (furnitureAssemblyId && excludeAssemblyIds.includes(furnitureAssemblyId)) {
        return;
      }

      const rotatedBox = {
        center: furniture.center,
        width: furniture.width,
        thickness: furniture.thickness,
        rotation: furniture.rotation
      };

      const { centerLines, boxBoundingLines } = createBoxParallelAbsorbSegs(rotatedBox);
      centerLines.forEach((centerLine) => {
        absorbCenterLines.push(centerLine);
      });
      boxBoundingLines.forEach((boxBoundingLine) => {
        absorbBoxBoundingLines.push(boxBoundingLine);
      });
    });

    this._calculator.updateData({
      absorbBoxBoundingLines,
      absorbCenterLines
    });
  }

  calculateThenRender(rawBox: IRotatedBox) {
    this.clearModel();

    const absorbResult = this._calculator.calculate(rawBox);
    if (!absorbResult) return;

    rawBox.center = absorbResult.absorbPoint;
    rawBox.rotation = absorbResult.rotation;

    this.updateModel(absorbResult, rawBox);

    return absorbResult;
  }

  calculateOnly(rawBox: IRotatedBox) {
    // 组合吸附与 2D 移动(calculateThenRender)保持一致:按最近的包围盒边线吸附,不强制旋转顶边朝墙
    const absorbResult = this._calculator.calculate({ ...rawBox });
    if (!absorbResult) return;

    return absorbResult;
  }

  private updateModel(absorbResult: IAbsorbResult, box: IRotatedBox) {
    this._sublineModel.update(convertParallelAbsorbSeb(absorbResult.absorbSegInfos, box));
  }

  clearModel() {
    this._sublineModel.clear();
  }

  dispose() {
    this._sublineModel.dispose();
  }
}
